import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { Table } from 'primeng/table';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AgencyService } from 'src/app/services/agency.service';
import { InfoReportService } from 'src/app/services/info-report.service';
import { LeCaseNumberService } from 'src/app/services/le-case-number.service';
import { LookupService } from 'src/app/services/lookup.service';
import { PeopleService } from 'src/app/services/people.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';
import { NESSValidators } from '../../../shared/validators/ness.validators';

@Component({
  selector: 'ness-events-leads-targets',
  templateUrl: './events-leads-targets.component.html'
})
export class EventsLeadsTargetsComponent implements OnDestroy, OnInit {

  private _filterFormValidator: ValidatorFn = (formGroup: FormGroup): ValidationErrors | null => {
    let isValid: boolean = true;

    if (formGroup && formGroup.getRawValue()) {
      switch (this.filterCtrlType) {
        case 'FilterInput':
          isValid = !(formGroup.get('FilterInput').value == null || formGroup.get('FilterInput').value.length === 0);
          break;
        case 'SSN':
          isValid = formGroup.get('FilterSSN').value.replace(/[_|-]/g, '').length > 0;
          break;
        case 'Name':
          isValid = formGroup.get('FilterFirstNameInput').value.replace(' ', '').concat(formGroup.get('FilterLastNameInput').value.replace(' ', '')).length > 0;
          break;
        case 'DateOfBirth':
          isValid = !(formGroup.get('FilterDate').value == null || formGroup.get('FilterDate').value.length === 0);
          break;
        case 'PhoneNumber':
          isValid = formGroup.get('FilterPhoneNumber').value.replace(/[_|-]/g, '').length > 0;
          break;
      }

    }
    return isValid ? null : { 'filterFormInvalid': true };
  };

  @Output() eltSubmitted = new EventEmitter<any>();
  maxDateRule: Date = new Date();
  agenciesByStateId: ServerModels.ENESS_REF_Agency[] = [];
  caseNumbersByAgencyId: ServerModels.ENESS_XREF_LECaseNumbers[] = [];
  caseNumbersTextFiltered: ServerModels.ENESS_XREF_LECaseNumbers[] = [];
  crimeGunIds: ServerModels.CrimeGunSelectionValues[] = [];
  eventsLeadsTargetsData: any;
  eventsLeadsTargetsForm: FormGroup = this.fb.group({
    IntelligenceReportID: '',
    ENESS_XREF_IRsCrimeGuns_Primary: this.fb.array([]),
    ENESS_XREF_IRsCrimeGuns_Related: this.fb.array([]),
    ENESS_XREF_IRsLECaseNumbers: this.fb.array([]),
    ENESS_XREF_IRsPeopleTargets: this.fb.array([])
  });
  filterCtrlType: string = 'FilterInput';
  filterTypes: ServerModels.ENESS_PersonFilters[];
  findAPersonForm: FormGroup = this.fb.group({
    FilterID: ['', Validators.required],
    FilterInput: '',
    FilterDate: '',
    FilterSSN: '',
    FilterFirstNameInput: '',
    FilterLastNameInput: '',
    FilterPhoneNumber: ''
  }, { validator: this._filterFormValidator });
  leCaseNumberForm: FormGroup = this.fb.group({
    StateID: ['', Validators.required],
    AgencyID: [{ value: '', disabled: true }, Validators.required],
    LECaseNumberID: [{ value: '', disabled: true }, Validators.required]
  }, { validator: NESSValidators.requireDisabled });
  loading: boolean = false;
  people: ServerModels.ENESS_XREF_IRsPeopleTargets[] = [];
  peopleSearchResults: ServerModels.ENESS_PersonDTO[] = [];
  peopleSearchResultTableCols: any[];
  person: ServerModels.ENESS_Person;
  peopleFilterValues: ServerModels.FilterValues[] = [];
  rows: number = AppConstants.defaultTableRows;
  selectedCrimeGunIds: ServerModels.CrimeGunSelectionValues[] = [];
  selectedPeople: ServerModels.ENESS_Person[] = [];
  showFilterInputCtrl: boolean;
  showFilterDateCtrl: boolean;
  showFilterSSNCtrl: boolean;
  showFilterNameCtrl: boolean;
  showFilterPhoneNumberCtrl: boolean;

  private _infoReportUpdatedSub$: Subscription;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  @ViewChild('ptt', { static: false }) peopleTargetsTable: Table;

  get ENESS_XREF_IRsCrimeGuns_Primary(): FormArray {
    return this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsCrimeGuns_Primary') as FormArray;
  }

  get ENESS_XREF_IRsCrimeGuns_Related(): FormArray {
    return this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsCrimeGuns_Related') as FormArray;
  }

  get ENESS_XREF_IRsLECaseNumbers(): FormArray {
    return this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsLECaseNumbers') as FormArray;
  }

  _infoReportLECaseNumbers: ServerModels.IRLECaseNumbersViewModel[] = [];
  get infoReportLECaseNumbers(): ServerModels.IRLECaseNumbersViewModel[] {
    return this._infoReportLECaseNumbers;
  }

  set infoReportLECaseNumbers(value: ServerModels.IRLECaseNumbersViewModel[]) {
    this._infoReportLECaseNumbers = value;
  }

  constructor(private infoReportService: InfoReportService,
    private peopleService: PeopleService,
    public lookupService: LookupService,
    private agencyService: AgencyService,
    private leCaseNumberService: LeCaseNumberService,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    public messageService: MessageService) {

   this._infoReportUpdatedSub$ = this.infoReportService.infoReportUpdated$.subscribe((updatedIR) => this._updateFormData(updatedIR));
  }

  ngOnInit() {

    this.peopleSearchResultTableCols = [
      { field: 'ENESS_XREF_PeopleFBIFileNumbers', subfield: 'FBIFileNumber', header: 'FBI #' },
      { field: 'ENESS_XREF_PeopleStateIDNumbers', subfield: 'StateIDNumbers', header: 'State ID' },
      { field: 'LastName', header: 'Last Name' },
      { field: 'FirstName', header: 'First Name' },
      { field: 'DateOfBirth', header: 'Birth Date' },
      { field: 'SSN', header: 'SSN' }
    ];


    this.findAPersonForm.get('FilterID').valueChanges.subscribe(
      (filterId:string) => {
        this._onPersonFilterValueChange(filterId);
      }
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._infoReportUpdatedSub$.unsubscribe();
  }

  private _onPersonFilterValueChange(filterId: string): void {
    if (filterId === 'Name' || filterId === 'SSN' || filterId === 'DateOfBirth' || filterId === 'PhoneNumber') {
      this.filterCtrlType = filterId;
    } else {
      this.filterCtrlType = 'FilterInput';
    }
    this.findAPersonForm.updateValueAndValidity();
  }

  private _updateFormData(ir: ServerModels.ENESS_IntelligenceReports) {
    if (this.eventsLeadsTargetsForm) {
      this.peopleService.personFilterTypes$.pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (pFilters: ServerModels.ENESS_PersonFilters[]) => {
          this.filterTypes = pFilters;
        }
      });

      ir.ENESS_XREF_IRsCrimeGuns.forEach(cgn => {
        let cg = this.fb.group({
          IRCrimeGunID: cgn.IRCrimeGunID,
          IntelligenceReportID: cgn.IntelligenceReportID,
          CrimeGunNumberID: cgn.CrimeGunNumberID,
          CrimeGunNumber: cgn.CrimeGunNumber,
          ENESS_CrimeGunNumbers: this.fb.group({
            CrimeGunNumberID: cgn.CrimeGunNumberID,
            LegacyATFGunNumber: (cgn.ENESS_CrimeGunNumbers?.LegacyATFGunNumber || cgn.LegacyATFGunNumber)
          }),
          NumberOfLinkedShootings: cgn.NumberOfLinkedShootings || 0,
          IsPrimary: cgn.IsPrimary
        });

        if (cgn.IsPrimary) {
          (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsCrimeGuns_Primary')).push(cg);
        } else {
          (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsCrimeGuns_Related')).push(cg);
        }
      });

      ir.ENESS_XREF_IRsLECaseNumbers.forEach(lec => {
        let cn = this.fb.group({
          IRLECaseNumberID: lec.IRLECaseNumberID,
          IntelligenceReportID: lec.IntelligenceReportID,
          LECaseNumberID: lec.LECaseNumberID,
          LECaseNumber: lec.LECaseNumber,
          StateID: lec.StateID,
          AgencyID: lec.AgencyID,
          ENESS_REF_State: this.fb.group({
            StateID: lec.ENESS_REF_State.StateID,
            StateAbbreviation: lec.ENESS_REF_State.StateAbbreviation
          }),
          ENESS_REF_Agency: this.fb.group({
            AgencyID: lec.ENESS_REF_Agency.AgencyID,
            AgencyName: lec.ENESS_REF_Agency.AgencyName
          })
        });

        (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsLECaseNumbers')).push(cn);
      });

      ir.ENESS_XREF_IRsPeopleTargets.forEach(pt => {
        let tgt = this.fb.group({
          IRPeopleTargetsID: pt.IRPeopleTargetsID,
          IntelligenceReportID: pt.IntelligenceReportID,
          PersonID: pt.PersonID,
          ENESS_Person: this.fb.group({
            PersonID: pt.ENESS_Person.PersonID,
            FirstName: pt.ENESS_Person.FirstName,
            LastName: pt.ENESS_Person.LastName,
            DateOfBirth: pt.ENESS_Person.DateOfBirth,
            SSN: pt.ENESS_Person.SSN
          })
        });
        (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsPeopleTargets')).push(tgt);
        this.people.push(pt);
      })

      this.eventsLeadsTargetsForm.patchValue({
        IntelligenceReportID: ir.IntelligenceReportID
      });

      if (this.peopleTargetsTable)
        this.peopleTargetsTable.reset();

      this.GetIRLECaseNumbers(ir.IntelligenceReportID);
    }
  }

  private _updateLECaseNumberState() {
    if (this.caseNumbersByAgencyId.length == 0) {
      this.leCaseNumberForm.get('LECaseNumberID').disable();
    } else {
      this.leCaseNumberForm.get('LECaseNumberID').enable();
    }

    if (this.agenciesByStateId.length == 0) {
      this.leCaseNumberForm.get('AgencyID').disable();
    } else {
      this.leCaseNumberForm.get('AgencyID').enable();
    }
  }

  public searchCrimeGunIds(event: { originalEvent: Event, query:string }) {
    this.infoReportService.getCrimeGunIds(event.query).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (results: ServerModels.CrimeGunSelectionValues[]) => {
        this.crimeGunIds = results.map((cg) => {
          return {
            CrimeGunNumberID: cg.CrimeGunNumberID,
            CrimeGunNumber: cg.CrimeGunNumber,
            NumberOfLinkedShootings: cg.NumberOfLinkedShootings,
            ENESS_CrimeGunNumbers: {
              LegacyATFGunNumber: cg.LegacyATFGunNumber,
            }
          };
        });
      }
    });
  }

  searchAgencies(stateId: string) {
    if (stateId) {

      this.agencyService.getAgenciesForState(stateId).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (agency: ServerModels.ENESS_REF_Agency[]) => {
          this.caseNumbersByAgencyId.length = 0;
          this.agenciesByStateId.length = 0;
          this.agenciesByStateId = [...agency];
          this._updateLECaseNumberState();

        }
      });
    }
  }

  searchCaseNumbers(agencyId: string) {
    this.leCaseNumberForm.get('LECaseNumberID').reset();
    if (agencyId) {
      this.leCaseNumberService.getLECaseNumberByAgency(agencyId).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (cn: ServerModels.ENESS_XREF_LECaseNumbers[]) => {
          this.caseNumbersByAgencyId.length = 0;
          this.caseNumbersByAgencyId = [...cn];
          this._updateLECaseNumberState();
        }
      });
    }
  }

  filterLECaseNumberSingle(event: { originalEvent: Event, query: string }) {
    let query = event.query;

    let formData = this.eventsLeadsTargetsForm.getRawValue();
    let leCasesInCurrentIR: ServerModels.ENESS_XREF_IRsLECaseNumbers[] = [];
    formData['ENESS_XREF_IRsLECaseNumbers'].forEach((x: ServerModels.ENESS_XREF_IRsLECaseNumbers) => {
      leCasesInCurrentIR.push(x);
    });
    this.caseNumbersTextFiltered = this.leCaseNumberService.filterCaseNumbers(query, this.caseNumbersByAgencyId, leCasesInCurrentIR);
  }

  searchPerson() {

    this.peopleFilterValues = [];
    this.peopleSearchResults = [];

    const filterIDCtrl = this.findAPersonForm.get('FilterID');
    const filterInputCtrl = this.findAPersonForm.get('FilterInput');
    const filterDateCtrl = this.findAPersonForm.get('FilterDate');
    const filterSSNCtrl = this.findAPersonForm.get('FilterSSN');
    const filterFirstNameCtrl = this.findAPersonForm.get('FilterFirstNameInput');
    const filterLastNameCtrl = this.findAPersonForm.get('FilterLastNameInput');
    const filterPhoneNumberCtrl = this.findAPersonForm.get('FilterPhoneNumber');
    
    let searchValue = '';

    // Get search value
    // Calendar control for Date of Birth
    // input control for the other types
    switch (filterIDCtrl.value) {
      case "DateOfBirth":
        searchValue = filterDateCtrl.value;
        break;
      case "SSN":
        searchValue = filterSSNCtrl.value;
        break;
      case "PhoneNumber":
        let phone = filterPhoneNumberCtrl.value;
        if (!!phone) {
          phone = phone.trim().replace(/\D/g, ''); // keep only digits
          if (phone.length > 3) {
            searchValue = phone;
          }
        }       
        break;
      case "Name":
        if (!!filterFirstNameCtrl.value && !!filterLastNameCtrl.value) {
          searchValue = filterFirstNameCtrl.value + " " + filterLastNameCtrl.value;
        } else if (!!filterFirstNameCtrl.value) {
          searchValue = "FirstName:" + filterFirstNameCtrl.value.trim();
        } else if (!!filterLastNameCtrl.value) {
          searchValue = "LastName:" + filterLastNameCtrl.value.trim();
        }
        break;
      default:
        searchValue = filterInputCtrl.value;
    }

    // Create API payload
    this.filterTypes.forEach(x => {
      if (x.FilterColumnName != null) {

        let peopleFilterValue = {
          FilterColumnName: x.FilterColumnName,
          FilterValue: x.FilterColumnName == filterIDCtrl.value ? searchValue : null
        } as ServerModels.FilterValues;

        if (peopleFilterValue.FilterValue != null && peopleFilterValue.FilterValue != "")
          this.peopleFilterValues.push(peopleFilterValue);
      }
    });

    if (this.peopleFilterValues != null && this.peopleFilterValues.length > 0) {
      // Call search people API
      this.peopleService.findPeople(this.peopleFilterValues).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (people: ServerModels.ENESS_PersonDTO[]) => {
          this.peopleSearchResults = people;
        },
        complete: () => {
          this.findAPersonForm.reset({
            FilterID: ''
          })
          this.resetFilterCtrl();
          this.showFilterInputCtrl = true;
        }
      });
    }
  }

  addSelectedLECaseNumber() {
    let formData = this.leCaseNumberForm.value;
    let lecn = this.caseNumbersByAgencyId.find(x => x.LECaseNumberID == formData.LECaseNumberID.LECaseNumberID);
    let state = this.lookupService.usStatesSubj.getValue().find((x: ServerModels.ENESS_REF_State) => x.StateID == formData.StateID);
    let agency = this.agenciesByStateId.find(x => x.AgencyID == formData.AgencyID);
    let cn = {
      LECaseNumberID: lecn.LECaseNumberID,
      LECaseNumber: lecn.LECaseNumber,
      StateID: state.StateID,
      AgencyID: agency.AgencyID,
      ENESS_REF_State: state,
      ENESS_REF_Agency: agency
    } as ServerModels.ENESS_XREF_IRsLECaseNumbers;

    (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsLECaseNumbers')).push(this.fb.group(cn));
    this.agenciesByStateId = [];
    this.caseNumbersByAgencyId = [];
    this.leCaseNumberForm.reset({
      StateID: '',
      AgencyID: { value: '', disabled: true },
      LECaseNumberID: { value: '', disabled: true }
    });

    if (cn.LECaseNumberID != undefined && cn.LECaseNumberID != null) {
      this.infoReportService.getIRLECaseNumbersByLECaseNumberIDs(cn.LECaseNumberID).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (result: ServerModels.IRLECaseNumbersViewModel) => {
          if (result != null) {
            this.infoReportLECaseNumbers.push(result);
            this.cdRef.detectChanges();
          }
        }
      });
    }
  }
  addSelectedPerson() {
    const addPeople: ServerModels.ENESS_XREF_IRsPeopleTargets[] = this.selectedPeople.map(x => {
      let peopleTarget = {
        PersonID: x.PersonID,
        ENESS_Person: x
      } as ServerModels.ENESS_XREF_IRsPeopleTargets;
      return peopleTarget;
    });

    this.people = [...this.people, ...addPeople];
    this.selectedPeople = [];
    this.peopleSearchResults = [];
  }

  removePerson(idx: number) {
    this.people.splice(idx, 1);
    this.people = [...this.people];
  }

  onAddCrimeGun(evt:Event, ctrl: AutoComplete) {
    if (!ctrl.value)
      return;
    if (evt.target['id'] == 'addPrimaryCG') {
      (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsCrimeGuns_Primary')).push(this.fb.group({
        CrimeGunNumberID: ctrl.value.CrimeGunNumberID,
        CrimeGunNumber: ctrl.value.CrimeGunNumber,
        ENESS_CrimeGunNumbers: this.fb.group({ LegacyATFGunNumber: ctrl.value.ENESS_CrimeGunNumbers.LegacyATFGunNumber }),
        NumberOfLinkedShootings: ctrl.value.NumberOfLinkedShootings,
        IsPrimary: true
      }));

    } else {
      (<FormArray>this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsCrimeGuns_Related')).push(this.fb.group({
        CrimeGunNumberID: ctrl.value.CrimeGunNumberID,
        CrimeGunNumber: ctrl.value.CrimeGunNumber,
        ENESS_CrimeGunNumbers: this.fb.group({ LegacyATFGunNumber: ctrl.value.ENESS_CrimeGunNumbers.LegacyATFGunNumber }),
        NumberOfLinkedShootings: ctrl.value.NumberOfLinkedShootings,
        IsPrimary: false
      }));
    }
    ctrl.value = ctrl.inputEL.nativeElement.value = null;
    this.crimeGunIds = [];
  }

  onSubmit() {
    if (this.eventsLeadsTargetsForm.valid) {

      const formData = this.eventsLeadsTargetsForm.value;
      formData['ENESS_XREF_IRsPeopleTargets'] = this.people;

      let crimeGuns = formData.ENESS_XREF_IRsCrimeGuns_Primary.concat(formData.ENESS_XREF_IRsCrimeGuns_Related);

      this.eventsLeadsTargetsData = {
        formData: Object.assign({
          ENESS_XREF_IRsCrimeGuns: crimeGuns,
          ENESS_XREF_IRsLECaseNumbers: [],
          ENESS_XREF_IRsPeopleTargets: [],
        }, formData),
        tab: 'eventsLeads'
      };

      this.eltSubmitted.emit(this.eventsLeadsTargetsData);

    }
  }

  private resetFilterCtrl() {
    this.showFilterInputCtrl = false;
    this.showFilterDateCtrl = false;
    this.showFilterSSNCtrl = false;
    this.showFilterNameCtrl = false;
    this.showFilterPhoneNumberCtrl = false;
  }

  public GetIRLECaseNumbers(intelligenceReportID: string) {
    let aFormArray = this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsLECaseNumbers') as FormArray;

    if (aFormArray.value.length > 0) {
      if (!AppUtils.isEmptyInputValue(intelligenceReportID)) {
        this.infoReportService.getIRLECaseNumbersByIRID(intelligenceReportID).pipe(takeUntil(this._unsubscribe$)).subscribe({
          next: (leCaseNumbers: ServerModels.IRLECaseNumbersViewModel[]) => {
            if (leCaseNumbers != null) {
              this.infoReportLECaseNumbers = leCaseNumbers;
              this.cdRef.detectChanges();
            }
          }
        })
      } else {
        (<ServerModels.ENESS_XREF_IRsLECaseNumbers[]>aFormArray.value).forEach(x => {
          this.infoReportService.getIRLECaseNumbersByLECaseNumberIDs(x.LECaseNumberID).pipe(takeUntil(this._unsubscribe$)).subscribe({
            next: (leCases: ServerModels.IRLECaseNumbersViewModel) => {
              this.infoReportLECaseNumbers.push(leCases);
              this.cdRef.detectChanges();
            }
          })
        });
      }
    }
  }

  public DeleteLECaseNumber(index: number, LECaseNumberID: string) {
    let aFormArray = this.eventsLeadsTargetsForm.get('ENESS_XREF_IRsLECaseNumbers') as FormArray;
    if (aFormArray.value.length > 0) {
      aFormArray.value.forEach((value: ServerModels.ENESS_XREF_IRsLECaseNumbers, i:number) => {
        if (value.LECaseNumberID == LECaseNumberID) aFormArray.removeAt(i);
      });
    }

    this.infoReportLECaseNumbers.splice(index, 1);
  }
}
